import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { ComponentAccessUserService } from 'src/app/core/services/utelize/system/component-access/component-access-user-service';
import { ConfigSystemParams, DgAlignment, DgColumnDataType, DgColumnWidths, DgSelectionMode, eSimApiPaths } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DgActionColumn } from 'src/app/core/interfaces/datagrid/dg-action-column';
import { DgApiPath } from 'src/app/core/interfaces/datagrid/dg-api-paths';
import { DgColumn } from 'src/app/core/interfaces/datagrid/dg-column';
import { DgPopup } from 'src/app/core/interfaces/datagrid/dg-pop-up';
import { DgProperties } from 'src/app/core/interfaces/datagrid/dg-properties';
import { EncryptionService } from 'src/app/core/services/utelize/general/encryption-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { firstValueFrom } from 'rxjs';
import { GridViewDefaults } from 'src/app/core/grid-view/component/grid-view.defaults';
import { GridViewComponent } from 'src/app/core/grid-view/component/grid-view.component';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';

const apiPath             =environment.eSimApi+eSimApiPaths.InviewUsers;
const exportFilenameSuffix="Inview-User-Data";
const popUpTitle          ="Inview User"; 

@Component({
  selector: 'app-inview-users-embedded',
  templateUrl: './inview-users.component.html',
  styleUrls: ['./inview-users.component.scss']
})

export class InviewUsersEmbeddedComponent implements OnInit {
  
  @Input() inviewCompanyId: number | undefined;
  @Input() portalUsers    : boolean | undefined;
  
  @Output() gridRowClick   : EventEmitter<number>=new EventEmitter<number>();
  @Output() gridRowDblClick: EventEmitter<number>=new EventEmitter<number>();
  
  @ViewChild('gridView', { static: false }) gridView!: GridViewComponent;
  
  private gridViewDefaults!: GridViewDefaults;
  
  public curUser        : SystemUserDTO;
  public dgApiPath!     : DgApiPath | undefined;
  public dgActionColumn!: DgActionColumn | undefined;
  public dgColumns!     : Array<DgColumn>;
  public dgPopup!       : DgPopup | undefined;
  public dgProperties!  : DgProperties | undefined;
  public gotDgProperties: boolean=false;
  
  constructor(
    private authenticationService     : AuthenticationService,
    private componentAccessUserService: ComponentAccessUserService,
    private configSystemService       : ConfigSystemService,
    private encryptionService         : EncryptionService,
    private exceptionsService         : ExceptionsService
  ) {
    this.curUser         =this.authenticationService.currentUserObject();
    this.gridViewDefaults=new GridViewDefaults(this.authenticationService, this.componentAccessUserService, this.configSystemService, this.exceptionsService);
  }

  public async ngOnInit(): Promise<void> {
    try {
      if (this.inviewCompanyId == undefined) {
        throw new Error("InviewUsersEmbeddedComponent - CompanyID cannot be determined");
      }

      await this.setUpDgApiPaths();
      this.setUpDgActionColumn();
      this.setUpDgColumns();
      this.setUpDgPopup();
      this.setUpDgProperties();

      this.gotDgProperties=true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private async setUpDgApiPaths(): Promise<void> {
    try {
      this.dgApiPath            =this.gridViewDefaults.setUpDgApiPaths(apiPath);
      this.dgApiPath!.refreshGet=() => this.setUpDgApiPathGet();

      await this.setUpDgApiPathGet();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async setUpDgApiPathGet(): Promise<void> {
    try {
      const encResult           =await firstValueFrom(this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>this.inviewCompanyId, true));
      const inviewCompanyIdEncSS=encResult.result;

      this.dgApiPath!.get=environment.eSimApi +
                          eSimApiPaths.InviewUsers + '/' +
                          eSimApiPaths.InviewUsersGetUsersServerSide +
                          "?inviewCompanyIdEncSS=" + inviewCompanyIdEncSS +
                          (this.portalUsers==undefined ? "" : "&portalUsers=" + this.portalUsers);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgActionColumn() {
    try {
      this.dgActionColumn         =this.gridViewDefaults.setUpDgActionColumn();
      this.dgActionColumn!.visible=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgPopup() {
    try {
      this.dgPopup=this.gridViewDefaults.setUpDgPopup(popUpTitle);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgProperties() {
    try {
      this.dgProperties                  =this.gridViewDefaults.setUpDgProperties("", exportFilenameSuffix + "-" + this.inviewCompanyId);
      this.dgProperties!.cacheEnabled    =this.configSystemService.getValueAsBoolean(ConfigSystemParams.GridCacheEnabled);
      this.dgProperties!.gridKey         ="EmbeddedInviewUsers";
      this.dgProperties!.pageSize        =this.configSystemService.getValueAsInteger(ConfigSystemParams.GridEmbeddedInviewUsersPageSize, 10);
      this.dgProperties!.remoteOperations=this.configSystemService.getValueAsBoolean(ConfigSystemParams.UseServerSidePagingInviewUsersEmbedded);
      this.dgProperties!.selectionMode   =DgSelectionMode.single;
      this.dgProperties!.showAddButton   =false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgColumns() {
    try {
      let dgColumn: DgColumn | undefined;
      
      //ID
      dgColumn             =this.gridViewDefaults.setUpDgColumn("id", "ID", 0, DgColumnDataType.number);
      dgColumn!.gridVisible=this.configSystemService.getValueAsBoolean(ConfigSystemParams.GridShowId, false);
      dgColumn!.width      =DgColumnWidths.id;
      this.dgColumns       =[dgColumn!];

      //companyId
      dgColumn             =this.gridViewDefaults.setUpDgColumn("companyId", "CompanyID", "", DgColumnDataType.number);
      dgColumn!.gridVisible=false;
      this.dgColumns.push(dgColumn!);

      //username
      dgColumn       =this.gridViewDefaults.setUpDgColumn("username", "Username", "", DgColumnDataType.string);
      dgColumn!.width=DgColumnWidths.username;
      this.dgColumns.push(dgColumn!);

      //email
      dgColumn=this.gridViewDefaults.setUpDgColumn("email", "Email", "", DgColumnDataType.string);
      this.dgColumns.push(dgColumn!);

      //firstName
      dgColumn       =this.gridViewDefaults.setUpDgColumn("firstName", "Firstname", "", DgColumnDataType.string);
      dgColumn!.width=DgColumnWidths.firstName;
      this.dgColumns.push(dgColumn!);

      //surname
      dgColumn       =this.gridViewDefaults.setUpDgColumn("surname", "Surname", "", DgColumnDataType.string);
      dgColumn!.width=DgColumnWidths.surname;
      this.dgColumns.push(dgColumn!);

      //cli
      dgColumn           =this.gridViewDefaults.setUpDgColumn("cli", "Mobile", "", DgColumnDataType.string);
      dgColumn!.alignment=DgAlignment.right;
      dgColumn!.width    =DgColumnWidths.mobileNumber;
      this.dgColumns.push(dgColumn!);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public _gridRowClick(rowId: number){
    try {
      this.gridRowClick.emit(rowId);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public _gridRowDblClick(rowId: number){
    try {
      this.gridRowDblClick.emit(rowId);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
